import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Fab, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

interface FloatingActionMenuProps {
  children: React.ReactNode;
  label?: string;
}

const FloatingActionMenu: React.FC<FloatingActionMenuProps> = ({
  children,
  label = "Actions",
}) => {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const queueCloseMenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, 220);
  }, [clearCloseTimer]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  return (
    <Box
      onMouseEnter={openMenu}
      onMouseLeave={queueCloseMenu}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 1.5,
        // Capture pointer events only while open so the FAB, the gap between it
        // and the action buttons, and the buttons themselves form one contiguous
        // hover region (prevents the menu collapsing as the cursor crosses the
        // gap). When closed, stay transparent to clicks so page content behind
        // the fixed container remains interactive.
        pointerEvents: open ? "auto" : "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1.5,
          transform: open ? "translateY(0)" : "translateY(12px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "transform 180ms ease, opacity 180ms ease",
        }}
      >
        {children}
      </Box>

      <Tooltip title={label} placement="left" arrow>
        <Fab
          color="primary"
          size="medium"
          aria-label={label}
          onMouseEnter={openMenu}
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            pointerEvents: "auto",
            width: 56,
            height: 56,
            minHeight: 56,
          }}
        >
          <MenuIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default FloatingActionMenu;
